import { api } from "@/modules/api";
import { BaseError } from "@/domains/errors";
import type { Uuid } from "@/domains/global/identifiers";
import type { TranscriptArrayItem } from "@/modules/audio-recording/transcript-types";

export type ProcessAudioRecordingArgs = {
  audioRecordingId: Uuid;
  transcriptContent: string;
  associatedNoteMarkdownContent: string;
  associatedSessionId: Uuid;
  audioDurationSeconds?: number | null;
  transcriptArray?: TranscriptArrayItem[];
  // TODO(@macromackie): This option is not yet wired up to the API. Follow up to implement.
  forceTranscriptRegeneration?: boolean;
};

export type ProcessAudioRecordingResult = {
  suggestedMarkdownContent: string;
};

export type ProcessAudioRecordingRefineArgs = {
  audioRecordingId: Uuid;
  transcriptContent: string;
  associatedNoteMarkdownContent: string;
  associatedSessionId: Uuid;
  userPrompt: string;
  audioDurationSeconds?: number | null;
  transcriptArray?: TranscriptArrayItem[];
};

export const processAudioRecording = async ({
  audioRecordingId,
  transcriptContent,
  associatedNoteMarkdownContent,
  associatedSessionId,
  audioDurationSeconds,
  transcriptArray,
  forceTranscriptRegeneration: _forceTranscriptRegeneration,
}: ProcessAudioRecordingArgs): Promise<ProcessAudioRecordingResult> => {
  const requestBody = {
    transcript_content: transcriptContent,
    associated_note_markdown_content: associatedNoteMarkdownContent,
    associated_session_id: associatedSessionId,
    ...(() => {
      if (typeof audioDurationSeconds !== "number" || !Number.isFinite(audioDurationSeconds)) {
        return {};
      }

      return { audio_duration_seconds: audioDurationSeconds };
    })(),
    ...(() => {
      if (!transcriptArray?.length) {
        return {};
      }

      return {
        associated_transcript_data: {
          transcript_text: transcriptContent,
          transcript_array: transcriptArray,
        },
      };
    })(),
  };

  const response = await api.post("/v2/audio-recordings/{audio_recording_id}/process", {
    params: {
      path: { audio_recording_id: audioRecordingId },
    },
    body: requestBody,
  });

  if (!response.response.ok) {
    const errorBody = response.error as { detail?: string } | undefined;
    const detail = errorBody?.detail || "Failed to process audio recording";
    throw new BaseError({
      message: detail,
      info: { audioRecordingId },
    });
  }

  const suggestedMarkdownContent = response.data?.suggested_markdown_content;
  if (!suggestedMarkdownContent) {
    throw new BaseError({
      message: "No suggested content returned from server",
      info: { audioRecordingId },
    });
  }

  return { suggestedMarkdownContent };
};

export const processAudioRecordingRefine = async ({
  audioRecordingId,
  transcriptContent,
  associatedNoteMarkdownContent,
  associatedSessionId,
  userPrompt,
  audioDurationSeconds,
  transcriptArray,
}: ProcessAudioRecordingRefineArgs): Promise<ProcessAudioRecordingResult> => {
  const requestBody = {
    transcript_content: transcriptContent,
    associated_note_markdown_content: associatedNoteMarkdownContent,
    associated_session_id: associatedSessionId,
    user_prompt: userPrompt,
    ...(() => {
      if (typeof audioDurationSeconds !== "number" || !Number.isFinite(audioDurationSeconds)) {
        return {};
      }

      return { audio_duration_seconds: audioDurationSeconds };
    })(),
    ...(() => {
      if (!transcriptArray?.length) {
        return {};
      }

      return {
        associated_transcript_data: {
          transcript_text: transcriptContent,
          transcript_array: transcriptArray,
        },
      };
    })(),
  };

  const response = await api.post("/v2/audio-recordings/{audio_recording_id}/refine/process", {
    params: {
      path: { audio_recording_id: audioRecordingId },
    },
    body: requestBody,
  });

  if (!response.response.ok) {
    const errorBody = response.error as { detail?: string } | undefined;
    const detail = errorBody?.detail || "Failed to refine audio recording";
    throw new BaseError({
      message: detail,
      info: { audioRecordingId },
    });
  }

  const suggestedMarkdownContent = response.data?.suggested_markdown_content;
  if (!suggestedMarkdownContent) {
    throw new BaseError({
      message: "No suggested content returned from server",
      info: { audioRecordingId },
    });
  }

  return { suggestedMarkdownContent };
};
